import { normalizePreserveCase } from 'components/Dashboard/utilities/normalizeString';
import dynamicTextImage from 'components/Dashboard/Influence/CrmEmails/TinyEditor/dynamicTextImage';

// preserveUnrecognizedFields: the composer (normalizeCrmEmailModel.js) always has the team's full
// mailMergeFields list, so a chip it can't resolve really is orphaned/stale and gets flagged as
// uppercase text. Read-only interaction views (CrmInteractionNewContent, InteractionCard,
// normalizeInteractionHtmlBody) have no such list - without it, a perfectly valid custom-field pill
// (e.g. "@nick-name") would look "unrecognized" and get destroyed. Pass true there so only the
// known-legacy shapes (built-in fields, old SVG/img icons) are touched and everything else is left
// exactly as authored.
export const validatePlaceholders = ({ emailBody, mailMergeFields = [], preserveUnrecognizedFields = false }) => {
  let htmlBody = document.createElement('div');
  htmlBody.innerHTML = emailBody;
  const normalizedFields = new Set([
    'salutation',
    ...mailMergeFields.map((field) => normalizePreserveCase(field)).filter((field) => field),
  ]);

  // Older composer versions suffixed built-in fields with "_text" (e.g. "salutation_text") -
  // treat those the same as their current unsuffixed name.
  const resolveFieldName = (normalizedId) => {
    if (!normalizedId) return null;
    if (normalizedFields.has(normalizedId)) return normalizedId;
    const withoutTextSuffix = normalizedId.replace(/_text$/i, '');
    return normalizedFields.has(withoutTextSuffix) ? withoutTextSuffix : null;
  };

  // Even older composer versions rendered the salutation/first name/full name fields as a plain
  // <img id="salutation_text" src=".../salutation_small.svg"> icon instead of a span - swap those
  // for the current "@fieldname" pill too. Matching on id alone would risk destroying an ordinary
  // pasted/content image that happens to share an id (e.g. "salutation") with a mail-merge field -
  // every legacy icon in production is served from this CDN path, so require that too.
  htmlBody.querySelectorAll('img[id]').forEach((img) => {
    const imgId = img.getAttribute('id');
    if (imgId === 'email_signature' || imgId === 'nosignature') return;
    const imgSrc = img.getAttribute('src') || '';
    if (!imgSrc.startsWith('https://cdn.policymogul.com/images/')) return;
    const resolvedFieldName = resolveFieldName(normalizePreserveCase(imgId));
    if (!resolvedFieldName) return;
    const replacementWrapper = document.createElement('div');
    replacementWrapper.innerHTML = dynamicTextImage(resolvedFieldName);
    img.parentNode?.replaceChild(replacementWrapper.firstElementChild, img);
  });

  const mailMergeSpansWithIds = htmlBody.querySelectorAll('span.crm-mail-merge-substitution');
  const otherSpansWithIds = htmlBody.querySelectorAll('span[id]:not(.crm-mail-merge-substitution)');

  const spansWithDataCrmMailMergeSubstitutionId = htmlBody.querySelectorAll(
    'span[data-crm-mail-merge-substitution-id]'
  );

  const spansWithIds = [...mailMergeSpansWithIds, ...otherSpansWithIds, ...spansWithDataCrmMailMergeSubstitutionId];

  spansWithIds.forEach((span) => {
    const spanId = span.getAttribute('id');
    const normalizedSpanId = normalizePreserveCase(spanId);
    const isSpecialId = spanId === 'email_signature' || spanId === 'nosignature';

    const dataCrmMailMergeSubstitutionId = span.getAttribute('data-crm-mail-merge-substitution-id');
    const normalizedDataCrmMailMergeSubstitutionId = normalizePreserveCase(dataCrmMailMergeSubstitutionId);

    //OP: data-crm-mail-merge-substitution-id exists case
    if (dataCrmMailMergeSubstitutionId) {
      const resolvedFieldName = resolveFieldName(normalizedDataCrmMailMergeSubstitutionId);
      // A modern chip's content is plain "@fieldname" text with no child elements - a hybrid legacy
      // chip (e.g. crm_email 970 on team 13) still nests the old SVG icon despite already having this
      // attribute, so the id alone can't tell them apart.
      const hasLegacyMarkup = span.children.length > 0;
      if (!resolvedFieldName) {
        if (!preserveUnrecognizedFields) {
          const uppercaseText = dataCrmMailMergeSubstitutionId.replace(/-/g, '_').toUpperCase();
          const textNode = document.createTextNode(uppercaseText);
          span.parentNode?.replaceChild(textNode, span);
        }
      } else if (hasLegacyMarkup) {
        const replacementWrapper = document.createElement('div');
        replacementWrapper.innerHTML = dynamicTextImage(resolvedFieldName);
        span.parentNode?.replaceChild(replacementWrapper.firstElementChild, span);
      } else if (resolvedFieldName !== normalizedDataCrmMailMergeSubstitutionId) {
        span.setAttribute('data-crm-mail-merge-substitution-id', resolvedFieldName);
      }
      return;
      //OP: id exists case
    } else if (span.getAttribute('id') && !isSpecialId && span.classList.contains('crm-mail-merge-substitution')) {
      const resolvedFieldName = resolveFieldName(normalizedSpanId);
      if (resolvedFieldName) {
        // Legacy chips (plain or "_text"-suffixed ids) rendered an SVG icon rather than today's
        // plain-text "@fieldname" pill - rebuild it so it displays consistently with current emails.
        const replacementWrapper = document.createElement('div');
        replacementWrapper.innerHTML = dynamicTextImage(resolvedFieldName);
        span.parentNode?.replaceChild(replacementWrapper.firstElementChild, span);
      } else if (!preserveUnrecognizedFields) {
        const uppercaseText = spanId.replace(/-/g, '_').toUpperCase();
        const textNode = document.createTextNode(uppercaseText);
        span.parentNode?.replaceChild(textNode, span);
      }
      return;
      //OP: crm-mail-merge-substitution className exists only case
    } else if (span.classList.contains('crm-mail-merge-substitution')) {
      const textElements = span.querySelectorAll('text');
      let text = '';
      if (textElements.length > 0) {
        text = Array.from(textElements)
          .map((textElement) => textElement.textContent)
          .join('');
      }
      const uppercaseText = text.replace(/-/g, '_').replace(/@/g, '').toUpperCase();
      const textNode = document.createTextNode(uppercaseText);
      span.parentNode?.replaceChild(textNode, span);
      return;
    }
  });

  return htmlBody.innerHTML;
};

export const applyEmailSignatureToBody = ({ emailAddressItem, emailBody, disableChange }) => {
  if (emailAddressItem?.signature) {
    let htmlBody = document.createElement('div');
    htmlBody.innerHTML = emailBody;
    let htmlSignature = htmlBody.querySelector('#email_signature');
    //OP: #nosignature span - means signature was removed on purpose
    let signatureRemovedSpan = htmlBody.querySelector('#nosignature');
    let disableSignatureChange = disableChange && htmlSignature;
    if (!disableSignatureChange && !signatureRemovedSpan) {
      if (!htmlSignature) {
        htmlSignature = document.createElement('div');
        htmlSignature.id = 'email_signature';
      }
      htmlSignature.innerHTML = emailAddressItem.signature;
      htmlBody.appendChild(htmlSignature);
      return htmlBody.innerHTML;
    } else {
      return emailBody;
    }
  } else {
    let htmlBody = document.createElement('div');
    htmlBody.innerHTML = emailBody;
    let htmlSignature = htmlBody.querySelector('#email_signature');
    if (htmlSignature) {
      htmlSignature.remove();
    }
    return htmlBody.innerHTML;
  }
};

export const toggleSignature = ({ emailAddressItem, emailBody }) => {
  let htmlBody = document.createElement('div');
  htmlBody.innerHTML = emailBody;
  let signatureRemovedSpan = htmlBody.querySelector('#nosignature');
  if (signatureRemovedSpan) {
    if (emailAddressItem?.signature) {
      let htmlSignature = document.createElement('div');
      htmlSignature.id = 'email_signature';
      htmlSignature.innerHTML = emailAddressItem.signature;
      htmlBody.appendChild(htmlSignature);
      let signatureRemovedSpanContainer = signatureRemovedSpan.parentNode;
      if (signatureRemovedSpanContainer) {
        signatureRemovedSpanContainer.parentNode.removeChild(signatureRemovedSpanContainer);
      }
      return htmlBody.innerHTML;
    } else {
      return emailBody;
    }
  } else {
    let htmlSignature = htmlBody.querySelector('#email_signature');
    if (htmlSignature) {
      htmlSignature.parentNode.removeChild(htmlSignature);
    }
    let signatureRemovedSpan = document.createElement('span');
    signatureRemovedSpan.id = 'nosignature';
    signatureRemovedSpan.style.visibility = 'hidden';
    htmlBody.appendChild(signatureRemovedSpan);
    return htmlBody.innerHTML;
  }
};
